// ── REDUCER — l'unico posto dove lo stato dell'app cambia ───────────────────
import { LS } from './lib/helpers.js';
import { INIT_FILTERS } from './lib/constants.js';
import { TODAY, DOY, YR } from './lib/dateState.js';
export function reducer(state,action){
  let s={...state};
  switch(action.type){
    case "SET_THEME":
      s.myTheme=action.theme;
      LS.s("oncer_theme",action.theme);
      return s;
    case "SET_PROFILE":
      s.profile={...s.profile,...action.data};
      LS.s("oncer_profile",s.profile);
      return s;
    // Carica le ricompense vere dal database, quelle collegate all'account.
    // NON sostituisce mai "unlocked"/"claimed" di netto: li UNISCE con quello
    // che c'era già in locale. Motivo: il salvataggio verso il database
    // (vedi updateRewards in App.jsx) parte in background ogni volta che le
    // ricompense cambiano, ma non è garantito che finisca prima che l'app
    // venga richiusa — se succede, la prossima volta che l'app riparte
    // leggerebbe dal database una versione un po' vecchia. Sovrascrivere di
    // netto in quel caso cancellerebbe per sempre una ricompensa già
    // sbloccata (bug segnalato da Matte: lo sticker "scintille" sparito
    // dopo aver sbloccato "stella"). Unire invece i due elenchi è sempre
    // sicuro, perché sono elenchi che crescono soltanto: uno sblocco non
    // viene mai tolto. Gli altri campi (decorazioni equipaggiate, ecc.)
    // seguono invece il valore arrivato dal database, com'era prima.
    case "SET_REWARDS":{
      const incoming=action.rewards||{};
      const current=s.rewards||{unlocked:[],claimed:[],equippedDecorations:[],decorationPositions:{}};
      s.rewards={
        ...current,
        ...incoming,
        unlocked:Array.from(new Set([...(current.unlocked||[]),...(incoming.unlocked||[])])),
        claimed:Array.from(new Set([...(current.claimed||[]),...(incoming.claimed||[])])),
      };
      LS.s("oncer_rewards",s.rewards);
      // L'ordine dello scaffale (30/08/2026): prima viveva SOLO in localStorage, mai
      // sincronizzato — chi seguiva un altro utente non poteva mai vedere il vero ordine
      // dei suoi libri, solo quello (vuoto/default) letto dal proprio dispositivo. Ora viaggia
      // dentro "rewards" (lo stesso jsonb già sincronizzato su Supabase da updateRewards in
      // App.jsx), con lo stesso principio già usato per le decorazioni: il valore dal database
      // vince su quello locale quando arriva un fetch vero (vedi commento sopra).
      if(Array.isArray(s.rewards.shelfOrder)){
        s.shelfOrder=s.rewards.shelfOrder;
        LS.s("oncer_shelforder",s.shelfOrder);
      }
      return s;
    }
    // Un altro account ha appena fatto accesso su questo stesso
    // browser/dispositivo — tutto quello che era in cache locale (nome,
    // bio, foto profilo, copertina, tema, libri, micro-libri, ecc.)
    // apparteneva all'account precedente e non deve mai comparire, nemmeno
    // per un istante, sotto il nuovo account. Si riparte da zero pulito;
    // i dati veri del nuovo account arriveranno subito dopo dal database.
    case "RESET_LOCAL_CACHE":{
      // Le ricompense/adesivi ORA hanno un salvataggio vero collegato
      // all'account (colonna profiles.rewards, vedi fetchRewards/updateRewards
      // in api.js) — quindi qui si può ripulire la cache locale in sicurezza:
      // appena parte il caricamento del nuovo account, SET_REWARDS scarica le
      // sue vere ricompense dal database. Senza questa pulizia, le ricompense
      // del vecchio account rimarrebbero visibili (e verrebbero persino unite
      // a quelle del nuovo, per via dell'unione fatta in SET_REWARDS) finché
      // il caricamento non arriva — un istante in cui l'account sbagliato
      // vedrebbe ricompense non sue.
      const keysToClear=["oncer_theme","oncer_profile","oncer_books","oncer_micro","oncer_threads","oncer_nbstyle","oncer_nbpaper","oncer_settings","oncer_filters","oncer_stats","oncer_shelforder","oncer_feedviewed","oncer_rewards"];
      keysToClear.forEach(k=>{ try{ localStorage.removeItem(k); }catch(e){} });
      s.rewards={unlocked:[],claimed:[],equippedDecorations:[],decorationPositions:{},dailyStreak:{count:0,lastDay:null,lastYear:null}};
      s.myTheme="neon";
      s.profile={username:"tu",bio:""};
      s.books={};
      s.micro=[];
      s.threads=[];
      s.nbStyle="cursive";
      s.nbPaper="ivory";
      s.settings={pushNotif:true,emailNotif:false,notifTime:"20:00",whoCanMessage:"everyone",libraryPublic:false,plan:"free"};
      s.filters=INIT_FILTERS;
      s.stats={reactionsGivenToday:0,reactionsGivenDate:null};
      s.shelfOrder=[];
      s.lastFeedViewedAt=0;
      s.sharedBooks=[];
      s.activeEvents=[];
      s.pendingInvites=[];
      s.followedMicroPages=[];
      s.memory=null;
      return s;
    }
    case "LOAD_PAGES":{
      // Replaces local books wholesale with what's really in the database —
      // called once right after login/signup, before the user touches anything.
      s.books=action.books;
      LS.s("oncer_books",s.books);
      return s;
    }
    case "LOAD_FEED":{
      // Solo contenuti veri, mai riempitivi finti — un feed vuoto per un
      // account nuovo deve sembrare vuoto, non un finto brulicare di gente.
      // Non persistito in localStorage — si ricarica sempre da capo.
      s.feed=action.items;
      return s;
    }
    case "LOAD_MICRO":{
      // Real micro-libri replace the illustrative demo seed once we know
      // what the user actually has — matches the pattern used for books.
      s.micro=action.items;
      LS.s("oncer_micro",s.micro);
      return s;
    }
    case "SET_UNREAD_THREADS":{
      s.unreadThreadsReal=action.count;
      return s;
    }
    case "LOAD_NOTIFICATIONS":{
      s.notifications=action.items;
      return s;
    }
    case "SET_UNREAD_NOTIF_COUNT":{
      s.unreadNotifCount=action.count;
      return s;
    }
    case "MARK_NOTIFS_READ_LOCAL":{
      s.notifications=s.notifications.map(n=>({...n,read:true}));
      s.unreadNotifCount=0;
      return s;
    }
    case "REMOVE_NOTIFICATION_LOCAL":{
      s.notifications=s.notifications.filter(n=>n.id!==action.id);
      return s;
    }
    // Una notifica "di sistema" (es. obiettivo sbloccato) appena creata sul
    // database va mostrata subito in campanella, senza aspettare il
    // prossimo ricaricamento — qui si aggiorna anche il contatore non lette.
    case "ADD_NOTIFICATION_LOCAL":{
      s.notifications=[action.notif,...s.notifications];
      s.unreadNotifCount=(s.unreadNotifCount||0)+1;
      return s;
    }
    case "SET_FEED_VIEWED":{
      s.lastFeedViewedAt=action.ts;
      LS.s("oncer_feedviewed",action.ts);
      return s;
    }
    case "LOAD_MEMORY":{
      s.memory=action.memory;
      return s;
    }
    case "LOAD_SHARED_BOOKS":{
      s.sharedBooks=action.items;
      return s;
    }
    case "LOAD_EVENTS":{
      s.activeEvents=action.items;
      return s;
    }
    case "LOAD_INVITES":{
      s.pendingInvites=action.items;
      return s;
    }
    case "LOAD_FOLLOWED_MICRO":{
      s.followedMicroPages=action.items;
      return s;
    }
    case "REMOVE_INVITE":{
      s.pendingInvites=s.pendingInvites.filter(i=>i.sharedBookId!==action.sharedBookId);
      return s;
    }
    case "ADD_SHARED_BOOK":{
      s.sharedBooks=[action.book,...s.sharedBooks];
      return s;
    }
    // Un libro EVENTO è stato chiuso da chi lo ha creato (close-event-book
    // Edge Function già eseguita con successo lato server) — qui si
    // aggiorna solo lo stato locale così l'interfaccia lo riflette subito,
    // senza dover ricaricare tutto da capo.
    case "CLOSE_SHARED_BOOK":{
      s.sharedBooks=s.sharedBooks.map(b=>String(b.id)===String(action.sharedBookId)?{...b,closed:true}:b);
      return s;
    }
    case "REMOVE_MICRO":{
      s.micro=s.micro.filter(m=>String(m.id)!==String(action.microId));
      LS.s("oncer_micro",s.micro);
      return s;
    }
    case "REMOVE_SHARED_BOOK":{
      s.sharedBooks=s.sharedBooks.filter(b=>String(b.id)!==String(action.sharedBookId));
      return s;
    }
    case "SHOW_TOAST":{
      s.toast={message:action.message,kind:action.kind||"error",id:Date.now()};
      return s;
    }
    case "HIDE_TOAST":{
      s.toast=null;
      return s;
    }
    case "ADD_PAGE":{
      const {photo,caption,saveMicroId,day,year,filterId,stickerId}=action;
      // toDiary: questa foto conta come pagina di oggi nel diario personale
      // (e quindi va anche nel Feed)? Non specificato => sì, per compatibilità
      // con qualunque altro punto del codice che dispacciasse ADD_PAGE senza
      // saperlo (oggi solo Capture.jsx lo fa, ed è sempre esplicito).
      const toDiary=action.toDiary!==false;
      const fid=filterId||"none";
      let micro=[...s.micro];
      if(saveMicroId){
        micro=micro.map(m=>String(m.id)===String(saveMicroId)?{...m,pages:m.pages+1}:m);
      }
      s.micro=micro;
      LS.s("oncer_micro",micro);
      if(fid!=="none"){
        s.filters=s.filters.map(f=>f.id===fid?{...f,uses:f.uses+1}:f);
        LS.s("oncer_filters",s.filters);
      }
      if(toDiary){
        // Solo quando la foto conta davvero come pagina di oggi del diario
        // personale si aggiorna il libro annuale e si pubblica sul Feed —
        // una foto scattata solo per un micro-libro/libro condiviso (toggle
        // spento) non deve segnare "già fatto oggi" né comparire nel Feed.
        const books={...s.books};
        if(!books[year])books[year]={pages:[]};
        if(!books[year].pages.some(p=>p.day===day)){
          const np={photo,caption,day,ts:Date.now(),filter:fid,sticker:stickerId||null};
          books[year]={...books[year],pages:[...books[year].pages,np]};
        }
        s.books=books;
        LS.s("oncer_books",books);
        // pageTs, se passato da chi ha dispacciato l'azione, lega questa voce
        // di Feed alla stessa pagina del diario — serve per poterla ritrovare
        // e aggiornare più avanti (vedi UPDATE_PAGE_PHOTO) quando l'upload in
        // background sostituisce la foto locale con quella vera su Storage.
        const nf={id:action.pageTs||Date.now(),user:s.profile.username||"tu",ut:s.myTheme,day,date:"oggi",photo,cap:caption,reactions:{},micro:null,filter:fid,sticker:stickerId||null};
        s.feed=[nf,...s.feed];
      }
      console.log("Oncer: pagina aggiunta —",photo.startsWith("data:")?`base64 locale (${Math.round(photo.length/1024)}KB) — upload su Storage in corso in background`:photo,"— diario:",toDiary);
      return s;
    }
    // Sostituisce, dopo il fatto, la foto locale (base64) mostrata subito
    // all'utente con l'URL vero caricato su Storage — così il diario/feed
    // non restano per sempre con una copia locale pesante in localStorage.
    // Se l'upload in background fallisce, questa azione semplicemente non
    // viene mai dispacciata: la copia locale resta visibile, nessun danno.
    case "UPDATE_PAGE_PHOTO":{
      const {year,day,feedId,photo}=action;
      if(year!=null&&day!=null&&s.books[year]){
        const pages=s.books[year].pages.map(p=>p.day===day?{...p,photo}:p);
        s.books={...s.books,[year]:{...s.books[year],pages}};
        LS.s("oncer_books",s.books);
      }
      if(feedId!=null){
        s.feed=s.feed.map(f=>f.id===feedId?{...f,photo}:f);
      }
      return s;
    }
    case "ADD_MICRO":{
      const {preset,title,theme,id,real}=action;
      const nm={id:id||Date.now(),real:!!real,preset:preset.id,icon:preset.icon,title,pages:0,max:20,closed:false,theme,cover:preset.icon,dates:TODAY.toLocaleDateString("it-IT",{day:"numeric",month:"short",year:"numeric"})};
      s.micro=[...s.micro,nm];
      LS.s("oncer_micro",s.micro);
      return s;
    }
    case "SEND_MSG":{
      const {threadId,text}=action;
      s.threads=s.threads.map(th=>th.id===threadId
        ? {...th,msgs:[...th.msgs,{from:"me",txt:text,time:"adesso"}]}
        : th
      );
      LS.s("oncer_threads",s.threads);
      return s;
    }
    case "MARK_READ":{
      const {threadId}=action;
      s.threads=s.threads.map(th=>th.id===threadId?{...th,unread:0}:th);
      LS.s("oncer_threads",s.threads);
      return s;
    }
    case "SHARE_PAGE":{
      const {threadId,page}=action;
      s.threads=s.threads.map(th=>th.id===threadId
        ? {...th,msgs:[...th.msgs,{from:"me",txt:null,time:"adesso",page}]}
        : th
      );
      LS.s("oncer_threads",s.threads);
      return s;
    }
    case "SET_BIO":{
      s.profile={...s.profile,bio:action.bio};
      LS.s("oncer_profile",s.profile);
      return s;
    }
    case "UPDATE_SETTINGS":{
      s.settings={...s.settings,...action.data};
      LS.s("oncer_settings",s.settings);
      return s;
    }
    case "SET_PLAN":{
      s.settings={...s.settings,plan:action.plan};
      LS.s("oncer_settings",s.settings);
      return s;
    }
    case "SET_NB_STYLE":{
      s.nbStyle=action.style;
      LS.s("oncer_nbstyle",action.style);
      return s;
    }
    case "SET_NB_PAPER":{
      s.nbPaper=action.paper;
      LS.s("oncer_nbpaper",action.paper);
      return s;
    }
    case "START_THREAD":{
      const {user}=action;
      if(s.threads.some(th=>th.user===user)) return s;
      const nt={id:"th_"+user+"_"+Date.now(),user,unread:0,msgs:[]};
      s.threads=[nt,...s.threads];
      LS.s("oncer_threads",s.threads);
      return s;
    }
    // Cancellazione dallo swipe nel Notebook — SOLO per le chat demo/locali (mai sincronizzate
    // col database, vivono solo in questo stato). Le conversazioni reali si "cancellano" invece
    // lato Supabase con hideThreadForUser (vedi src/lib/api.js) e vengono tolte dallo stato
    // locale realThreads in Notebook.jsx, non da qui. "oncer_experience" non si può mai
    // rimuovere: è la chat guida permanente, vedi ENSURE_ONCER_EXPERIENCE sopra.
    case "REMOVE_THREAD":{
      const {threadId}=action;
      if(threadId==="oncer_experience") return s;
      s.threads=s.threads.filter(th=>th.id!==threadId);
      LS.s("oncer_threads",s.threads);
      return s;
    }
    // Garantisce che la conversazione permanente "Oncer Experience" ci sia
    // sempre — controllata a ogni apertura del Notebook, non solo alla
    // prima installazione, così arriva anche a chi aveva già la cache
    // salvata da prima che esistesse.
    case "ENSURE_ONCER_EXPERIENCE":{
      // Pulizia attiva: chi ha già usato l'app prima di questa correzione
      // ha ancora le vecchie chat finte salvate nella memoria del browser
      // — cambiare solo il valore "di partenza" non le toglie da chi le
      // aveva già. Le rimuove per nome, ovunque si trovino.
      const FAKE_DEMO_USERS=["sofia_m","luca.wav","marco_r"];
      const cleaned=s.threads.filter(th=>!FAKE_DEMO_USERS.includes(th.user));
      const alreadyHasExperience=cleaned.some(th=>th.id==="oncer_experience");
      s.threads=alreadyHasExperience?cleaned:[action.thread,...cleaned];
      LS.s("oncer_threads",s.threads);
      return s;
    }
    // Stesso tipo di correzione di ENSURE_ONCER_EXPERIENCE, ma per i
    // filtri ufficiali: chi ha già usato l'app prima che aggiungessi gli
    // "Oncer Setup" ha ancora l'elenco filtri vecchio salvato in memoria
    // — aggiunge solo quelli ufficiali mancanti (per id), senza toccare
    // o duplicare i filtri creati dalla community o dall'utente stesso.
    case "ENSURE_OFFICIAL_FILTERS":{
      const existingIds=new Set(s.filters.map(f=>f.id));
      const missing=INIT_FILTERS.filter(f=>!existingIds.has(f.id));
      if(missing.length===0) return s;
      s.filters=[...s.filters,...missing];
      LS.s("oncer_filters",s.filters);
      return s;
    }
    // Segna come "già viste" le pagine dei micro-libri di chi segui che
    // stai per aprire — il pallino sulla campanella "chi segui" conta solo
    // quelle non ancora viste, non tutte quelle esistenti. Tiene solo le
    // ultime 300 per non far crescere la lista all'infinito.
    case "MARK_MICRO_PAGES_SEEN":{
      const newIds=action.ids||[];
      const merged=[...new Set([...(s.seenMicroPageIds||[]),...newIds])].slice(-300);
      s.seenMicroPageIds=merged;
      LS.s("oncer_seen_micro_pages",merged);
      return s;
    }
    case "ADD_FILTER":{
      const {name,cat,css,tint,vig,grain,vigAmt,grainAmt,id,real}=action;
      const nf={id:id||("f"+Date.now()),real:!!real,name,cat:cat||"Community",creator:s.profile.username||"tu",css,tint,vig,grain,vigAmt,grainAmt,uses:0,official:false,mine:true};
      s.filters=[nf,...s.filters];
      LS.s("oncer_filters",s.filters);
      return s;
    }
    case "LOAD_FILTERS":{
      // Real community filters (from every user) go alongside the built-in
      // presets; anything the app already has locally with the same id is
      // replaced so use-counts stay fresh rather than duplicating.
      const localOnly=s.filters.filter(f=>!f.real);
      const incomingIds=new Set(action.items.map(f=>f.id));
      s.filters=[...action.items,...localOnly.filter(f=>!incomingIds.has(f.id))];
      LS.s("oncer_filters",s.filters);
      return s;
    }
    case "REACT":{
      // emoji: la nuova reazione (null se si sta solo togliendo la
      // precedente). previousEmoji: la reazione che questa persona aveva
      // già messo su questa pagina, se ce n'era una — una persona può
      // avere una sola reazione attiva per pagina, mai più di una insieme.
      const {itemId,emoji,previousEmoji}=action;
      s.feed=s.feed.map(it=>{
        if(it.id!==itemId)return it;
        const reactions={...(it.reactions||{})};
        if(previousEmoji&&reactions[previousEmoji]){
          reactions[previousEmoji]=Math.max(0,reactions[previousEmoji]-1);
          if(reactions[previousEmoji]===0) delete reactions[previousEmoji];
        }
        if(emoji){
          reactions[emoji]=(reactions[emoji]||0)+1;
        }
        return {...it,reactions};
      });
      if(emoji){
        const prevStats=s.stats||{reactionsGivenToday:0,reactionsGivenDate:null};
        const sameDay=prevStats.reactionsGivenDate===DOY;
        s.stats={reactionsGivenDate:DOY,reactionsGivenToday:(sameDay?prevStats.reactionsGivenToday:0)+1};
        LS.s("oncer_stats",s.stats);
      }
      return s;
    }
    case "UNLOCK_REWARD":{
      const {rewardId}=action;
      if(s.rewards.unlocked.includes(rewardId)) return s;
      s.rewards={...s.rewards,unlocked:[...s.rewards.unlocked,rewardId]};
      LS.s("oncer_rewards",s.rewards);
      return s;
    }
    case "CLAIM_GOAL":{
      const {goalId,rewardId,filterReward}=action;
      if(s.rewards.claimed.includes(goalId)) return s;
      const unlocked=rewardId&&!s.rewards.unlocked.includes(rewardId)?[...s.rewards.unlocked,rewardId]:s.rewards.unlocked;
      s.rewards={...s.rewards,claimed:[...s.rewards.claimed,goalId],unlocked};
      if(filterReward){
        s.filters=s.filters.map(f=>f.id===filterReward?{...f,locked:false}:f);
        LS.s("oncer_filters",s.filters);
      }
      LS.s("oncer_rewards",s.rewards);
      return s;
    }
    // Segna che OGGI i 3 obiettivi giornalieri sono stati completati tutti —
    // dispacciata da App.jsx appena se ne accorge, una volta sola al giorno
    // (vedi il controllo lastDay/lastYear lì). Tiene una piccola serie di
    // giorni consecutivi "perfetti": se ieri (giorno DOY-1, stesso anno) era
    // già stato completato, la conta cresce; altrimenti riparte da 1. Caso
    // limite volutamente semplificato: un giorno 1 gennaio dopo un 31
    // dicembre completato interrompe la serie invece di continuarla — capita
    // una volta l'anno, non vale la complessità di gestire il cambio anno.
    case "MARK_DAILY_GOALS_COMPLETE":{
      const prev=s.rewards.dailyStreak||{count:0,lastDay:null,lastYear:null};
      if(prev.lastDay===DOY&&prev.lastYear===YR) return s; // già segnato oggi
      const consecutive=prev.lastYear===YR&&prev.lastDay===DOY-1;
      const dailyStreak={count:consecutive?prev.count+1:1,lastDay:DOY,lastYear:YR};
      s.rewards={...s.rewards,dailyStreak};
      LS.s("oncer_rewards",s.rewards);
      return s;
    }
    case "EQUIP_DECOR":{
      const {slot,itemId}=action; // slot: 0 or 1
      const equipped=[...(s.rewards.equippedDecor||[null,null])];
      equipped[slot]=itemId;
      s.rewards={...s.rewards,equippedDecor:equipped};
      LS.s("oncer_rewards",s.rewards);
      return s;
    }
    case "SET_SHELF_ORDER":{
      s.shelfOrder=action.order;
      LS.s("oncer_shelforder",action.order);
      // Specchiato dentro rewards per farlo viaggiare sullo stesso salvataggio automatico
      // verso Supabase (vedi updateRewards in App.jsx, parte ad ogni cambio di st.rewards) —
      // così chi ti segue vede il vero ordine con cui hai sistemato lo scaffale, non solo le
      // decorazioni (bug segnalato da Matte il 30/08/2026).
      s.rewards={...s.rewards,shelfOrder:action.order};
      LS.s("oncer_rewards",s.rewards);
      return s;
    }
    default: return s;
  }
}
